/**
 * Syncs Rebuy cart widgets with the theme cart.
 *
 * Containers live in `snippets/domaine-cart-integrations.liquid` (drawer + `/cart`)
 * and are opted out of morph via `data-skip-subtree-update`. Because morph no longer
 * remounts them, a widget that first loaded against an empty/stale cart stays hidden
 * until we refresh products.
 *
 * - Empty container, no live sibling with the same id → `Rebuy.init()`
 * - Already mounted → `widget.getWidgetProducts()` (never re-init; that destroys the grid)
 * - Product refresh waits for `rebuy:cart.*` so Rebuy's cart copy is current
 *
 * @typedef {object} RebuyWidget
 * @property {string | number} id
 * @property {(callback?: () => void) => void} [getWidgetProducts]
 *
 * @typedef {object} RebuyGlobal
 * @property {RebuyWidget[]} [widgets]
 * @property {() => void} [init]
 *
 * @typedef {Window & { Rebuy?: RebuyGlobal }} WindowWithRebuy
 */

/** @returns {RebuyGlobal | undefined} */
const getRebuy = () => /** @type {WindowWithRebuy} */ (window).Rebuy;

/** @returns {HTMLElement[]} */
const getContainers = () =>
  /** @type {HTMLElement[]} */ ([...document.querySelectorAll('[data-rebuy-id]')]);

/** @param {HTMLElement} el */
const isMounted = (el) => Boolean(el.childNodes.length || el.dataset.initialized !== undefined);

/**
 * @param {string} id
 * @returns {RebuyWidget | undefined}
 */
const findWidget = (id) => getRebuy()?.widgets?.find((w) => String(w.id) === String(id));

/**
 * `Rebuy.init()` is global per widget id. Drawer + cart page share one id — calling
 * init while a sibling is already live tears down that sibling's product grid.
 */
const mountEmptyContainers = () => {
  const containers = getContainers();
  const empty = containers.filter((el) => !isMounted(el));
  if (empty.length === 0) return;

  for (const el of empty) {
    const id = el.dataset.rebuyId;
    if (id && containers.some((other) => other !== el && other.dataset.rebuyId === id && isMounted(other))) {
      return;
    }
  }

  getRebuy()?.init?.();
};

const refreshMountedWidgets = () => {
  for (const el of getContainers()) {
    if (!isMounted(el)) continue;

    const id = el.dataset.rebuyId;
    if (!id) continue;

    const widget = findWidget(id);
    if (typeof widget?.getWidgetProducts === "function") {
      widget.getWidgetProducts();
    }
  }
};

document.addEventListener("cart:update", () => {
  requestAnimationFrame(mountEmptyContainers);
});

document.addEventListener("rebuy:cart.change", refreshMountedWidgets);
document.addEventListener("rebuy:cart.add", refreshMountedWidgets);

const syncOnRebuyReady = () => {
  mountEmptyContainers();
  refreshMountedWidgets();
};

document.addEventListener("rebuy.ready", syncOnRebuyReady);
document.addEventListener("rebuy.loaded", syncOnRebuyReady);

// This module is deferred (`fetchpriority="low"`), so Rebuy may already have
// fired ready/loaded before these listeners attach — sync once if it's live.
const rebuy = getRebuy();
if (rebuy?.widgets || typeof rebuy?.init === "function") {
  syncOnRebuyReady();
}
